import React, { useEffect, useState, useCallback } from "react"
import { Text, IconCheck, ProgressBar, Loader } from "dash-styled"
import {
    JobSubmitData,
    fromInputs
} from "../../../../../../../Models/Client/JobSubmitData"
import { ParseResult } from "../CSV/ParseResult"
import { JobSubmitDataParseStatus } from "../../../../../../../Models/Client/JobSubmitDataParseStatus"
import { JSONEditor } from "../../../../../Lazy/JSONEditor"
import { ErrorObject } from "ajv"
import { Errors } from "../../../../Common/Errors/Errors"
import { TrimmedWarning } from "../TrimmedWarning"

interface Props {
    testId?: string
    single?: boolean
    /**
     * Validation errors from a schema POV (not JSON syntax)
     */
    errors: ErrorObject[]
    /**
     * The data to be rendered in the text area
     */
    data?: JobSubmitData
    initialValue: unknown[]
    /**
     * When a syntactically valid JSON is changed,
     * the parent component can update state
     */
    onChange: (data: JobSubmitData) => void
    /**
     * Import event flag and stale callback
     */
    freshImport: boolean
    onLoadedImport: () => void
    parseState: JobSubmitDataParseStatus
    onClearFile: () => void
    onDownloadExample: () => void
}

export const JSONInput: React.FC<Props> = ({
    onChange,
    single = false,
    testId,
    data,
    initialValue,
    errors,
    freshImport,
    onLoadedImport,
    parseState,
    onClearFile,
    onDownloadExample
}: Props) => {
    const [parseErr, setParseErr] = useState<string | undefined>(undefined)

    const handleChange = useCallback(
        (value: unknown) => {
            onChange(fromInputs(Array.isArray(value) ? value : [], single))
            setParseErr(undefined)
        },
        [onChange, single]
    )

    useEffect(() => {
        if (freshImport) {
            onLoadedImport()
        }
    }, [freshImport, onLoadedImport])

    useEffect(() => {
        if (!parseErr) {
            const size = new TextEncoder().encode(JSON.stringify(data)).length
            if (size > 50 * 1000 * 1000) {
                onChange(fromInputs([], single))
                setParseErr(
                    "This file is too big - please make sure it's not over 50MB. (You can always upload multiple files)"
                )
            }
        }
    }, [data, parseErr, onChange, single])

    if (parseState.type !== "idle") {
        return (
            <div className="mx-6 mt-6">
                {parseState.type === "inProgress" && (
                    <>
                        <ProgressBar
                            fill={parseState.progress}
                            thickness="lg"
                            color="yellow"
                            noMargin={true}
                        />
                        <p className="text-sm text-rhino-900 mt-4 leading-3">
                            Processing
                        </p>
                    </>
                )}
                <ParseResult
                    fileType="json"
                    parseState={parseState}
                    onClear={onClearFile}
                    onDownloadExample={onDownloadExample}
                />
            </div>
        )
    }

    return (
        <div className="mx-6 mt-6">
            {single && <TrimmedWarning className="my-3" />}
            <div className="relative">
                <JSONEditor
                    initialValue={initialValue}
                    heightPx={375}
                    testId={testId}
                    onChange={handleChange}
                    fallback={<Loader size="xxxl" />}
                    onError={setParseErr}
                />
                {errors === undefined && (
                    <div className="absolute flex justify-center items-center top-0 right-20 pr-1">
                        <span
                            className="flex"
                            style={{
                                color: "#00c4ac"
                            }}
                        >
                            <IconCheck size="xl" />
                        </span>
                        <Text
                            color="coldgreen"
                            size="xs"
                            className="ml-1"
                            component="span"
                        >
                            Valid
                        </Text>
                    </div>
                )}
            </div>
            {parseErr !== undefined ? (
                <p
                    className="text-coral-700 text-xs bg-coral-200 p-2 rounded overflow-x-auto mt-2 font-mono whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: parseErr }}
                />
            ) : (
                <Errors errors={errors} showDataPath={true} />
            )}

            <Text size="sm" color="gray" className="text-right my-4 leading-4">
                Max file size: 50MB
            </Text>
        </div>
    )
}
